
import { useNavigate } from "react-router-dom";
import { Search, LayoutGrid, LayoutList, X } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { CandidateSearchResults } from "./CandidateSearchResults";
import { JobSearchResults } from "./JobSearchResults";
import { CompanySearchResults } from "./CompanySearchResults";
import { ViewMode } from "./types";
import {
  SearchDialog,
  SearchDialogContent,
  SearchDialogHeader,
  SearchDialogTitle,
} from "./SearchResultsDialog";

interface SearchResultsModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  searchQuery: string;
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
}

export function SearchResultsModal({
  isOpen,
  onOpenChange,
  searchQuery,
  viewMode,
  onViewModeChange
}: SearchResultsModalProps) {
  const navigate = useNavigate();
  
  return (
    <SearchDialog open={isOpen} onOpenChange={onOpenChange}>
      <SearchDialogContent className="w-[90vw] max-w-[90vw] h-[80vh] max-h-[80vh] p-0 gap-0">
        <div className="flex flex-col h-full">
          <SearchDialogHeader className="flex flex-row items-center justify-between px-6 py-4 border-b">
            <div className="flex items-center gap-2">
              <Search className="h-5 w-5 text-gray-500" />
              <SearchDialogTitle className="text-xl">Search Results for: <span className="font-semibold text-digger-blue">{searchQuery}</span></SearchDialogTitle>
            </div>
            <div className="flex items-center space-x-2 pr-8">
              <span className="text-sm font-medium text-gray-500 mr-2">View:</span>
              <ToggleGroup 
                type="single" 
                value={viewMode} 
                onValueChange={(value) => value && onViewModeChange(value as ViewMode)} 
                className="border rounded-md"
              >
                <ToggleGroupItem value="card" aria-label="Card view" className="data-[state=on]:bg-digger-blue data-[state=on]:text-white">
                  <LayoutGrid className="h-4 w-4" />
                </ToggleGroupItem>
                <ToggleGroupItem value="list" aria-label="List view" className="data-[state=on]:bg-digger-blue data-[state=on]:text-white">
                  <LayoutList className="h-4 w-4" />
                </ToggleGroupItem>
              </ToggleGroup>
            </div>
          </SearchDialogHeader>
          
          <div className="flex-grow overflow-hidden">
            <Tabs defaultValue="candidates" className="h-full flex flex-col">
              <TabsList className="mx-6 mt-3 mb-2 bg-gray-100">
                <TabsTrigger value="candidates" className="flex-1 py-2">
                  Candidates
                </TabsTrigger>
                <TabsTrigger value="jobs" className="flex-1 py-2">
                  Jobs
                </TabsTrigger>
                <TabsTrigger value="companies" className="flex-1 py-2">
                  Companies
                </TabsTrigger>
              </TabsList>
              
              <div className="flex-grow px-6 pb-6 overflow-hidden">
                <TabsContent value="candidates" className="h-full mt-0 overflow-y-auto border rounded-md">
                  <CandidateSearchResults 
                    searchQuery={searchQuery} 
                    onResultClick={(id) => {
                      onOpenChange(false);
                      navigate(`/candidates/${id}`);
                    }}
                    viewMode={viewMode}
                  />
                </TabsContent>
                
                <TabsContent value="jobs" className="h-full mt-0 overflow-y-auto border rounded-md">
                  <JobSearchResults 
                    searchQuery={searchQuery} 
                    onResultClick={(id) => {
                      onOpenChange(false);
                      navigate(`/jobs/${id}`);
                    }}
                    viewMode={viewMode}
                  />
                </TabsContent>
                
                <TabsContent value="companies" className="h-full mt-0 overflow-y-auto border rounded-md">
                  <CompanySearchResults 
                    searchQuery={searchQuery} 
                    onResultClick={(id) => {
                      onOpenChange(false);
                      navigate(`/companies/${id}`);
                    }}
                    viewMode={viewMode}
                  />
                </TabsContent>
              </div>
            </Tabs>
          </div>
        </div>
      </SearchDialogContent>
    </SearchDialog>
  );
}
